import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, Info, XCircle, X } from 'lucide-react';
import { useNotifications } from './NotificationProvider';

export function ToastStack() {
  const { items, dismiss } = useNotifications();
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const timerStartRef = useRef<Map<string, number>>(new Map());
  const pausedTimersRef = useRef<Map<string, { remaining: number; pausedAt: number }>>(new Map());

  // Handler de dismiss com animação
  const handleDismiss = useCallback((id: string) => {
    setExitingIds(prev => new Set(prev).add(id));
    // Limpar timer se existir
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    timerStartRef.current.delete(id);
    pausedTimersRef.current.delete(id);
    // Remover após animação
    setTimeout(() => {
      dismiss(id);
      setExitingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300); // Tempo da animação
  }, [dismiss]);

  // Auto-dismiss com timing otimizado e suporte a duration customizado
  useEffect(() => {
    items.forEach((item) => {
      if (!item.sticky && !exitingIds.has(item.id) && !timersRef.current.has(item.id)) {
        // Timing otimizado por tipo ou duration customizado
        const duration = item.duration ?? (
          item.type === 'error' ? 6000 :
          item.type === 'warning' ? 5000 :
          item.type === 'success' ? 4000 :
          3000 // info
        );
        
        const startTime = Date.now();
        timerStartRef.current.set(item.id, startTime);
        
        const timer = setTimeout(() => {
          handleDismiss(item.id);
        }, duration);
        
        timersRef.current.set(item.id, timer);
      }
    });
    
    return () => {
      timersRef.current.forEach(t => clearTimeout(t));
      timersRef.current.clear();
    };
  }, [items, exitingIds, handleDismiss]);

  // Handler de teclado (ESC para fechar último toast)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && items.length > 0) {
        const lastItem = items[items.length - 1];
        if (lastItem && !exitingIds.has(lastItem.id)) {
          handleDismiss(lastItem.id);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [items, exitingIds, handleDismiss]);

  // Handlers para pausar/resumir ao hover
  const pauseTimer = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    const startTime = timerStartRef.current.get(id);
    if (timer && startTime) {
      clearTimeout(timer);
      timersRef.current.delete(id);
      
      const item = items.find(i => i.id === id);
      if (item) {
        const duration = item.duration ?? (
          item.type === 'error' ? 6000 :
          item.type === 'warning' ? 5000 :
          item.type === 'success' ? 4000 :
          3000
        );
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(duration - elapsed, 0);
        pausedTimersRef.current.set(id, { remaining, pausedAt: Date.now() });
      }
    }
  }, [items]);

  const resumeTimer = useCallback((id: string) => {
    const paused = pausedTimersRef.current.get(id);
    if (paused) {
      pausedTimersRef.current.delete(id);
      const remaining = paused.remaining;
      if (remaining > 0) {
        const startTime = Date.now();
        timerStartRef.current.set(id, startTime);
        const timer = setTimeout(() => {
          handleDismiss(id);
        }, remaining);
        timersRef.current.set(id, timer);
      } else {
        // Se não há tempo restante, fechar imediatamente
        handleDismiss(id);
      }
    }
  }, [handleDismiss]);

  const getIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircle2 className="w-4 h-4 flex-shrink-0" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 flex-shrink-0" />;
      case 'error': return <XCircle className="w-4 h-4 flex-shrink-0" />;
      default: return <Info className="w-4 h-4 flex-shrink-0" />;
    }
  };

  const getStyles = (type: string, sticky: boolean) => {
    const base = 'transition-all duration-300 ease-out';
    switch (type) {
      case 'success':
        return `${base} bg-emerald-500/80 border-emerald-400/60 text-white shadow-md shadow-emerald-500/10`;
      case 'warning':
        return `${base} bg-amber-500/80 border-amber-400/60 text-white shadow-md shadow-amber-500/10`;
      case 'error':
        return sticky
          ? `${base} bg-red-600/85 border-red-500/70 text-white shadow-lg shadow-red-500/20 ring-2 ring-red-500/15`
          : `${base} bg-red-500/80 border-red-400/60 text-white shadow-md shadow-red-500/10`;
      default:
        return `${base} bg-sky-500/80 border-sky-400/60 text-white shadow-md shadow-sky-500/10`;
    }
  };

  return createPortal(
    <div className="fixed right-4 top-4 z-[9999] flex flex-col gap-2 max-w-[320px] pointer-events-none">
      <style>{`
        .toast-item { pointer-events: auto; }
      `}</style>
      {items.map((item) => {
        const isExiting = exitingIds.has(item.id);
        const duration = item.duration ?? (
          item.type === 'error' ? 6000 :
          item.type === 'warning' ? 5000 :
          item.type === 'success' ? 4000 :
          3000
        );
        
        return (
        <div
          key={item.id}
          onMouseEnter={() => pauseTimer(item.id)}
          onMouseLeave={() => resumeTimer(item.id)}
          className={`
            toast-item
            relative
            min-w-[240px] max-w-full
            px-3 py-2.5
            rounded-lg
            border
            backdrop-blur-sm
            flex items-start gap-2.5
            ${isExiting
              ? 'animate-out slide-out-to-right-full fade-out duration-300'
              : 'animate-in slide-in-from-right-full fade-in duration-300'
            }
            ${getStyles(item.type, item.sticky || false)}
          `}
        >
          {/* Ícone */}
          <div className="mt-0.5">
            {getIcon(item.type)}
          </div>

          {/* Conteúdo */}
          <div className="flex-1 min-w-0">
            {item.title && (
              <div className="font-semibold text-xs mb-0.5 leading-tight">
                {item.title}
              </div>
            )}
            <div className="text-xs leading-relaxed opacity-90">
              {item.message}
            </div>
            
            {/* Ações (se houver) */}
            {item.actions && item.actions.length > 0 && (
              <div className="flex gap-1.5 mt-2">
                {item.actions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      action.onClick();
                      dismiss(item.id);
                    }}
                    className="px-2 py-0.5 text-xs font-medium rounded bg-white/20 hover:bg-white/30 transition-colors"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Barra de progresso visual (apenas para não-sticky) */}
          {!item.sticky && !isExiting && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 rounded-b-lg overflow-hidden">
              <div 
                className="h-full bg-white/40"
                style={{ 
                  animation: `shrink-width ${duration}ms linear forwards`,
                }}
              />
            </div>
          )}

          {/* Botão Fechar - sempre visível, mas mais discreto em não-sticky */}
          <button
            onClick={() => handleDismiss(item.id)}
            className={`
              flex-shrink-0 p-1 rounded hover:bg-white/10 transition-colors
              ${item.sticky ? 'opacity-100' : 'opacity-60 hover:opacity-100'}
            `}
            aria-label="Fechar notificação"
            title="Fechar (ESC)"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )})}
    </div>,
    document.body
  );
}


